/**
 * ModalityLocationBadge
 *
 * Renders a location badge with coordinate popover on hover/focus.
 * Used by StatusBadges for the location capability chip.
 */

import React from 'react'
import { MapPin } from 'lucide-react'
import { BORDER, RADIUS, SHADOW, SURFACE, TEXT, TEXT_SIZE } from './design-tokens'
import { Tooltip } from './UIHelpers'
import type { ModalityLocationData } from './modality-types'

interface ModalityLocationBadgeProps {
  locationData?: ModalityLocationData | null | undefined
  trigger: React.ReactNode
  tooltipText?: string
  ariaLabel?: string
  precision?: number
  className?: string
}

export const ModalityLocationBadge: React.FC<ModalityLocationBadgeProps> = ({
  locationData,
  trigger,
  tooltipText = 'Location',
  ariaLabel = 'Show location coordinates',
  precision = 4,
  className = '',
}) => {
  const [showCoordinates, setShowCoordinates] = React.useState(false)

  const coordinates =
    locationData &&
    typeof locationData.latitude === 'number' &&
    typeof locationData.longitude === 'number'
      ? `${locationData.latitude.toFixed(precision)}, ${locationData.longitude.toFixed(precision)}`
      : null

  return (
    <div className={`relative inline-flex ${className}`}>
      <Tooltip text={tooltipText}>
        <button
          type="button"
          className="border-0 bg-transparent p-0 cursor-pointer"
          onMouseEnter={() => setShowCoordinates(true)}
          onMouseLeave={() => setShowCoordinates(false)}
          onFocus={() => setShowCoordinates(true)}
          onBlur={() => setShowCoordinates(false)}
          aria-label={ariaLabel}
        >
          {trigger}
        </button>
      </Tooltip>
      {showCoordinates && coordinates && (
        <div
          className={`absolute top-full left-1/2 z-50 mt-2 -translate-x-1/2 whitespace-nowrap border px-3 py-2 pointer-events-none animate-fade-in-up ${SURFACE.glassStrong} ${TEXT_SIZE.xs} font-mono ${RADIUS.md} ${BORDER.default} ${SHADOW.lg}`}
        >
          <div className="flex items-center gap-2">
            <MapPin className={`w-3 h-3 ${TEXT.secondary}`} />
            <span>{coordinates}</span>
          </div>
          {locationData?.city && (
            <div className={`${TEXT_SIZE.tiny} ${TEXT.muted} mt-1 text-center`}>
              {locationData.city}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ModalityLocationBadge